import React, { useCallback, useMemo } from "react";
import { toast } from "react-toastify";
import { ProjectContext, ProjectContextProps } from "./project-context-props.ts";
import useProjectsContext from "../projects/use-projects-context.ts";
import SimpleLoadingIndicator from "../../../components/simple-loading-indicator";
import useProjectLock from "../../../hooks/use-project-lock.ts";
import ProjectLock from "../../../models/classes/project-lock.ts";
import ProjectLockStatus from "../../../models/enums/project-lock-status.ts";
import useAuthenticationContext from "../authentication/use-authentication-context.ts";

interface ProjectContextProviderProps extends React.PropsWithChildren {
    projectId: string;
}

const ProjectContextProvider = ({ projectId, children }: ProjectContextProviderProps) => {
    const { projects, isLoading, refetchProjectIds, hydrateProjectLock } = useProjectsContext();
    const { authenticationResponse } = useAuthenticationContext();
    const { lockStatusQueryResult, addLockMutation, removeLockMutation } = useProjectLock(projectId);

    const project = useMemo(() => {
        if (isLoading || projects == null) return undefined;
        return projects.find(p => p.id === projectId)
    }, [isLoading, projects, projectId]);
    
    /* RAFRAICHISSEMENT IDS */
    
    const updateProjectIds = useCallback(async () => {
        await refetchProjectIds(projectId);
    }, [projectId, refetchProjectIds]);
    
    /* GESTION DU LOCK */

    const isProjectLocked = project?.projectLock?.isLocked;
    const userLockId = project?.projectLock?.userLockId;

    const projectLockStatus = useMemo(() => {
        if (!isProjectLocked) {
            return ProjectLockStatus.UNLOCK;
        }

        return userLockId === authenticationResponse?.user.id
            ? ProjectLockStatus.LOCK_OWNED
            : ProjectLockStatus.LOCK;
    }, [isProjectLocked, userLockId, authenticationResponse?.user.id]);

    const refetchLockStatus = useCallback(async () => {
        const query = await lockStatusQueryResult.refetch();
        let lockData: ProjectLock | undefined;
        if (query.data) {
            lockData = new ProjectLock(query.data);
            if (query.data?.id !== lockData.id)
                hydrateProjectLock(projectId, lockData);
        }

        return lockData;
    }, [lockStatusQueryResult, projectId, hydrateProjectLock])

    const lockProject = useCallback(async () => {
        const isLockedSuccessfully = await addLockMutation.mutateAsync();
        if (!isLockedSuccessfully) {
            toast.error('Une erreur est survenue lors du verrou du projet', { autoClose: 5000 });
            return;
        }
        
        const query = await lockStatusQueryResult.refetch();
        if (query.data) {
            const newLock = new ProjectLock(query.data);
            hydrateProjectLock(projectId, newLock);
        }
    }, [addLockMutation, projectId, lockStatusQueryResult, hydrateProjectLock]);

    const unlockProject = useCallback(async () => {
        const isUnlockedSuccessfully = await removeLockMutation.mutateAsync();
        if (!isUnlockedSuccessfully) {
            toast.error('Une erreur est survenue lors du déverrouillage du projet', { autoClose: 5000 });
            return;
        }
        
        const query = await lockStatusQueryResult.refetch();
        if (query.data) {
            const newLock = new ProjectLock(query.data);
            hydrateProjectLock(projectId, newLock);
        }
    }, [removeLockMutation, lockStatusQueryResult, hydrateProjectLock, projectId]);

    if (isLoading) return (
        <SimpleLoadingIndicator text='Chargement du projet en cours...' className='m-auto' />
    );

    if (project == null) return (
        <div>Le projet avec l'ID {projectId} n'a pas été trouvé</div>
    );

    const contextProps: ProjectContextProps = {
        project: project!,
        projectLockStatus,

        updateProjectIds,
        
        refetchLockStatus,
        lockProject,
        unlockProject,
    };

    return (
        <ProjectContext.Provider value={contextProps}>
            {children}
        </ProjectContext.Provider>
    )
}

export default ProjectContextProvider;